import { useState } from 'react'
import { Modal, Form, Radio, Input, Upload, Button, App, Space } from 'antd'
import { InboxOutlined } from '@ant-design/icons'
import type { UploadFile } from 'antd/es/upload/interface'
import { feedbackService } from '../../services/feedbackService'
import { errorService } from '../../services/errorService'
import { useAuth } from '../../hooks/useAuth'
import type {
  FeedbackFormData,
  ErrorContext
} from '../../types/feedback.types'

const { TextArea } = Input
const { Dragger } = Upload

/**
 * Props for FeedbackModal component
 */
export interface FeedbackModalProps {
  open: boolean
  onClose: () => void
  errorContext?: ErrorContext // If provided, pre-fill from error
}


/**
 * FeedbackModal Component
 *
 * Modal for submitting feedback to Rocket Pricing with two entry points:
 * 1. User-initiated: From Tools menu, allows Enhancement/Bug selection
 * 2. Error-initiated: From error modal, pre-filled with error context
 *
 * UX Requirements (Inara):
 * - 560px width modal
 * - Type Selection: Radio buttons (Enhancement/Bug) - locked to "Error" if from error
 * - Description: TextArea (4-5 rows), required, 10+ char minimum
 * - Screenshot: Optional upload via drag-drop or click (max 5MB, images only)
 * - Confirmation: Toast notification, modal auto-closes
 * - Full keyboard navigation, ARIA labels, focus management
 */
export const FeedbackModal: React.FC<FeedbackModalProps> = ({ open, onClose, errorContext }) => {
  const { modal, message } = App.useApp()
  const [form] = Form.useForm<FeedbackFormData>()
  const [submitting, setSubmitting] = useState(false)
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const { user } = useAuth()

  // Type is locked to 'Error' if called from error modal
  const isErrorMode = !!errorContext

  /**
   * Handle modal close - reset form and file list
   */
  const handleCancel = () => {
    form.resetFields()
    setFileList([])
    setSubmitting(false)
    onClose()
  }

  /**
   * Handle form submission
   */
  const handleSubmit = async (values: FeedbackFormData) => {
    // Check for org context - required for feedback submission
    if (!user?.currentOrganization) {
      modal.warning({
        title: 'Organization Required',
        content: 'Please select an organization before submitting feedback. You can switch organizations from the Account menu in the header.',
      })
      return
    }

    setSubmitting(true)

    // Disable "Report to Rocket Pricing" button during submission
    // to avoid showing it on errors from the submission itself
    const reEnableFeedback = errorService.disableFeedbackCallback()

    try {
      let attachmentKey: string | undefined
      let attachmentName: string | undefined

      // Step 1: Upload screenshot if provided
      if (values.screenshot) {
        const file = values.screenshot
        attachmentName = file.name

        // Get presigned URL
        const presignResult = await feedbackService.presignUpload(file.name)

        if (!presignResult.success || !presignResult.data) {
          message.error('Failed to prepare screenshot upload')
          setSubmitting(false)
          reEnableFeedback()
          return
        }

        // Upload to S3
        const uploadResult = await feedbackService.uploadToS3(
          presignResult.data.putUrl,
          file as File
        )

        if (!uploadResult.success) {
          message.error('Failed to upload screenshot')
          setSubmitting(false)
          reEnableFeedback()
          return
        }

        attachmentKey = presignResult.data.key
      }

      // Step 2: Submit feedback
      const feedbackResult = await feedbackService.createFeedback({
        feedbackType: values.feedbackType,
        feedbackComments: values.feedbackComments,
        attachmentKey,
        attachmentName,
        // Include error context if from error modal
        ...(errorContext && {
          errorCode: errorContext.errorCode,
          errorStack: errorContext.errorStack,
          eventId: errorContext.eventId,
          pageUrl: errorContext.pageUrl,
          browserInfo: errorContext.browserInfo
        })
      })

      if (!feedbackResult.success) {
        message.error('Failed to submit feedback. Please try again.')
        setSubmitting(false)
        reEnableFeedback()
        return
      }

      // Success!
      message.success('Feedback submitted successfully. Thank you!')
      reEnableFeedback()
      handleCancel() // Close modal and reset
    } catch (error: any) {
      console.error('Feedback submission error:', error)
      message.error('An unexpected error occurred. Please try again.')
      setSubmitting(false)
      reEnableFeedback()
    }
  }

  /**
   * Custom upload handler - prevent auto-upload
   */
  const handleUploadChange = ({ fileList: newFileList }: { fileList: UploadFile[] }) => {
    // Only allow one file
    const limitedFileList = newFileList.slice(-1)
    setFileList(limitedFileList)

    // Store file in form
    if (limitedFileList.length > 0 && limitedFileList[0].originFileObj) {
      form.setFieldValue('screenshot', limitedFileList[0].originFileObj)
    } else {
      form.setFieldValue('screenshot', undefined)
    }
  }

  /**
   * Validate file before upload
   */
  const beforeUpload = (file: File) => {
    const isImage = file.type.startsWith('image/')
    if (!isImage) {
      message.error('You can only upload image files!')
      return Upload.LIST_IGNORE
    }

    const isLt5M = file.size / 1024 / 1024 < 5
    if (!isLt5M) {
      message.error('Image must be smaller than 5MB!')
      return Upload.LIST_IGNORE
    }

    return false // Prevent auto-upload
  }

  /**
   * Initialize form values when modal opens
   */
  const getInitialValues = (): Partial<FeedbackFormData> => {
    if (isErrorMode) {
      return {
        feedbackType: 'Error',
        feedbackComments: '' // User adds "What were you trying to do?"
      }
    }
    return {
      feedbackType: 'Bug'
    }
  }

  return (
    <Modal
      title={isErrorMode ? 'Report Error to Rocket Pricing' : 'Submit Feedback to Rocket Pricing'}
      open={open}
      onCancel={handleCancel}
      width={560}
      footer={null}
      maskClosable={false}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={getInitialValues()}
        aria-label={isErrorMode ? 'Error report form' : 'Feedback submission form'}
      >
        {/* Feedback Type - Hidden when opened from error handler, but field still submitted */}
        <Form.Item
          name="feedbackType"
          label={isErrorMode ? undefined : "Type"}
          rules={[{ required: true, message: 'Please select feedback type' }]}
          hidden={isErrorMode}
        >
          <Radio.Group aria-label="Feedback type">
            <Radio value="Enhancement">Enhancement</Radio>
            <Radio value="Bug">Bug</Radio>
            {isErrorMode && <Radio value="Error">Error</Radio>}
          </Radio.Group>
        </Form.Item>

        {/* Description */}
        <Form.Item
          name="feedbackComments"
          label={isErrorMode ? 'What were you trying to do?' : 'Description'}
          rules={[
            { required: true, message: 'Please provide a description' },
            { min: 10, message: 'Description must be at least 10 characters' }
          ]}
        >
          <TextArea
            rows={5}
            placeholder={
              isErrorMode
                ? 'Describe what you were doing when the error occurred...'
                : 'Describe your feedback in detail...'
            }
            showCount
            maxLength={2000}
            aria-label="Feedback description"
          />
        </Form.Item>

        {/* Screenshot Upload */}
        <Form.Item
          name="screenshot"
          label="Screenshot (Optional)"
          valuePropName="file"
        >
          <Dragger
            fileList={fileList}
            onChange={handleUploadChange}
            beforeUpload={beforeUpload}
            maxCount={1}
            accept="image/*"
            aria-label="Screenshot upload"
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Click or drag image to upload</p>
            <p className="ant-upload-hint">
              Support for PNG, JPG, GIF, WebP. Max 5MB.
            </p>
          </Dragger>
        </Form.Item>

        {/* Error Context Display (if from error modal) */}
        {errorContext && (
          <Form.Item label="Error Details (Auto-Captured)">
            <div
              style={{
                padding: '12px',
                backgroundColor: '#f5f5f5',
                borderRadius: '4px',
                fontSize: '12px',
                fontFamily: 'monospace'
              }}
            >
              <div><strong>Error Code:</strong> {errorContext.errorCode}</div>
              <div><strong>Message:</strong> {errorContext.errorMessage}</div>
              {errorContext.eventId && (
                <div><strong>Event ID:</strong> {errorContext.eventId}</div>
              )}
              <div><strong>URL:</strong> {errorContext.pageUrl}</div>
            </div>
          </Form.Item>
        )}

        {/* Form Actions */}
        <Form.Item style={{ marginBottom: 0 }}>
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={handleCancel} disabled={submitting}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={submitting}>
              Submit Feedback
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default FeedbackModal
